import { motion } from "framer-motion";
import { CheckCircle2, Layers, Eye, Target, Zap, Shield } from "lucide-react";
import { useState, useEffect } from "react";
import { ContactModal } from "@/components/ContactModal";
import { useScrollAnimations } from "@/components/GSAPScrollAnimations";

const LogoDesignPrinciples = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollContainer = useScrollAnimations();

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Logo Design Principles",
      "description": "Master the fundamental design principles including simplicity, scalability, timelessness, and versatility. Learn how to apply these principles to create effective logos.",
      "author": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "url": "https://koech-creatives.onrender.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "logo": {
          "@type": "ImageObject",
          "url": "https://koech-creatives.onrender.com/og-image.png"
        }
      },
      "datePublished": "2026-07-13",
      "dateModified": "2026-07-13",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://koech-creatives.onrender.com/logo-design-principles"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <main ref={scrollContainer} className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Logo Design Principles
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            className="text-xl text-muted-foreground mb-8"
          >
            Master the fundamental principles that guide effective logo design. Learn how to apply these timeless principles to create logos that communicate, resonate, and endure.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Apply These Principles
          </motion.button>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            The Foundation of Great Logo Design
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Logo design principles are the foundational rules and guidelines that designers follow to create effective, memorable, and professional logos. These principles have been refined over decades of design practice and are used by the world's most successful brands.
          </motion.p>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground"
          >
            Understanding and applying these principles will help you create logos that not only look good but also serve their strategic purpose—building brand recognition, communicating brand values, and standing out in competitive markets.
          </motion.p>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            7 Core Logo Design Principles
          </motion.h2>
          <div className="space-y-8">
            {[
              { 
                icon: Eye, 
                title: "Simplicity", 
                desc: "Simple designs are more memorable, versatile, and easier to recognize. Remove unnecessary elements and focus on the essential. A simple logo works across all applications and scales effectively.",
                application: "Use minimal elements, clean lines, and avoid clutter."
              },
              { 
                icon: Target, 
                title: "Memorability", 
                desc: "A logo should be distinctive and easy to remember. Unique shapes, clever use of negative space, and unexpected elements create lasting impressions. Memorable logos stand out from competitors.",
                application: "Create unique designs that people can describe after seeing once."
              },
              { 
                icon: Layers, 
                title: "Versatility", 
                desc: "Your logo must work across all mediums and sizes—from business cards to billboards, from websites to mobile apps. It should be effective in color, black and white, and at any scale.",
                application: "Test your logo at various sizes and in different contexts."
              },
              { 
                icon: Zap, 
                title: "Timelessness", 
                desc: "Great logos avoid trendy elements that quickly become dated. Focus on enduring design principles rather than fleeting fads. Timeless logos remain relevant for decades.",
                application: "Avoid trendy fonts, colors, and styles that will date quickly."
              },
              { 
                icon: Shield, 
                title: "Appropriateness", 
                desc: "Your logo should reflect your brand's personality, industry, and target audience. The design should align with your brand positioning and resonate with your ideal customers.",
                application: "Consider your industry, audience, and brand personality."
              },
              { 
                icon: CheckCircle2, 
                title: "Scalability", 
                desc: "A good logo should be scalable without losing quality. It should work as a favicon on a website and as a large sign on a building. Vector-based design ensures crisp reproduction.",
                application: "Use vector design and test at extreme sizes."
              },
              { 
                icon: Layers, 
                title: "Balance", 
                desc: "Visual balance creates harmony and stability in your logo. Proper use of space, weight, and proportion ensures the design feels complete and professional.",
                application: "Distribute visual weight evenly and use space effectively."
              },
            ].map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <div className="flex gap-4 mb-4">
                  <principle.icon className="w-10 h-10 text-coral flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-xl mb-2">{principle.title}</h3>
                    <p className="text-muted-foreground mb-3">{principle.desc}</p>
                    <p className="text-sm text-coral font-medium">Application: {principle.application}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Color Principles */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Color Theory in Logo Design
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { color: "Primary Colors", desc: "Limit to 1-2 primary colors for maximum impact and recognition." },
              { color: "Color Psychology", desc: "Choose colors that align with your brand personality and industry." },
              { color: "Contrast", desc: "Ensure sufficient contrast for readability and accessibility." },
              { color: "Versatility", desc: "Test colors in both light and dark backgrounds." },
            ].map((item, index) => (
              <motion.div
                key={item.color}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-2">{item.color}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Typography Principles */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Typography Principles
          </motion.h2>
          <div className="space-y-4">
            {[
              { principle: "Readability", desc: "Choose fonts that are easy to read at all sizes." },
              { principle: "Appropriateness", desc: "Select fonts that match your brand personality." },
              { principle: "Simplicity", desc: "Avoid overly complex or decorative fonts." },
              { principle: "Customization", desc: "Consider custom lettering for uniqueness." },
              { principle: "Versatility", desc: "Ensure fonts work across all applications." },
              { principle: "Pairing", desc: "If using multiple fonts, ensure they complement each other." },
            ].map((item, index) => (
              <motion.div
                key={item.principle}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <Layers className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.principle}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Shape Principles */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Shape and Form Principles
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { shape: "Geometric Shapes", desc: "Circles, squares, and triangles create stability and order." },
              { shape: "Organic Shapes", desc: "Curves and natural forms create warmth and approachability." },
              { shape: "Negative Space", desc: "Clever use of empty space adds sophistication." },
              { shape: "Symmetry", desc: "Symmetrical designs feel stable and trustworthy." },
              { shape: "Asymmetry", desc: "Asymmetrical designs feel dynamic and modern." },
              { shape: "Proportion", desc: "Maintain pleasing ratios between elements." },
            ].map((item, index) => (
              <motion.div
                key={item.shape}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-2">{item.shape}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Applying Principles */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            How to Apply These Principles
          </motion.h2>
          <div className="space-y-6">
            {[
              { step: 1, title: "Research", desc: "Study successful logos in your industry and analyze how they apply these principles." },
              { step: 2, title: "Sketch", desc: "Create multiple concepts focusing on different principles." },
              { step: 3, title: "Refine", desc: "Apply principles systematically to improve each concept." },
              { step: 4, title: "Test", desc: "Validate your design against each principle." },
              { step: 5, title: "Iterate", desc: "Refine based on testing and feedback." },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="flex gap-4 items-start"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center text-coral font-bold">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Related Resources
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Ultimate Guide to Logo Design Services", url: "/logo-design-services-guide", desc: "Comprehensive guide to professional logo design" },
              { title: "What Makes a Good Logo", url: "/what-makes-a-good-logo", desc: "Learn the essential qualities of effective logos" },
              { title: "Minimalist Logo Design Guide", url: "/minimalist-logo-design-guide", desc: "Create powerful minimalist logos" },
              { title: "How to Design a Logo", url: "/how-to-design-a-logo", desc: "Step-by-step logo design process" },
            ].map((resource, index) => (
              <motion.a
                key={resource.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                href={resource.url}
                className="bg-card p-6 rounded-lg border border-gray-800 hover:border-coral transition-colors block"
              >
                <h3 className="font-semibold text-lg mb-2">{resource.title}</h3>
                <p className="text-muted-foreground">{resource.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Need Expert Logo Design?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-xl text-muted-foreground mb-8"
          >
            Our designers apply these principles to create logos that build brand recognition and drive business success.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get Your Logo Designed
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default LogoDesignPrinciples;
